"use client";

import { gsap, SplitText } from "@/mono/lib/gsap";
import useIsomorphicLayoutEffect from "@/mono/lib/hooks/useIsomorphicLayoutEffect";
import { useGlobalStore } from "@/mono/store/global";

import {
  createElement,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  Children,
} from "react";
import { useScrollRigScrollTrigger } from "../lib/hooks/useScrollRigScrollTrigger";
import { EASE_CUSTOM_1 } from "../lib/styles/easing";
import useResize from "../lib/hooks/useResize";

function CustomSplitText(
  {
    children,
    tag = "p",
    type = "lines",
    shouldAnimateOut = false,
    shouldRevert = true,
    className = "",
    animateOnScroll = true,

    // Expects {in, out, set}
    animation = null,

    animationType = "lineClipped",

    onScrollParams = {},

    immediate = false,

    ...props

    //TODO: hasParent
  },
  ref
) {
  const localRef = useRef();

  const splitRefParent = useRef();
  const splitRefChild = useRef();
  const windowSize = useGlobalStore((s) => s.windowSize);

  const visible = useRef(false);

  const lineClippedInAnimation = (params) => ({
    y: "0%",
    ease: params.ease ?? EASE_CUSTOM_1,
    duration: params.duration ?? 1,
    stagger: 0.1,
    delay: params?.delay ?? 0.4,
    clearProps: "all",
  });

  const lineClippedOutAnimation = (params) => ({
    y: "100%",
    ease: params.ease ?? EASE_CUSTOM_1,
    duration: params.duration ?? 1,
    stagger: 0.1,
    delay: params?.delay ?? 0.4,
    // clearProps: "all",
  });

  const animations = {
    lineClipped: {
      set: {
        y: "100%",
      },
      in: lineClippedInAnimation,
      out: lineClippedOutAnimation,
    },
  };

  const setAnimateIn = useCallback(() => {
    gsap.set(
      splitRefChild.current?.[type],
      animation ? animation.set : animations[animationType].set
    );
  }, []);

  function animateIn(params = {}) {
    visible.current = true;

    gsap.to(splitRefChild.current?.[type], {
      ...(animation
        ? animation.in(params)
        : animations[animationType].in(params)),
      ...params,

      onComplete: () => {
        params?.onComplete?.();
        if (!shouldRevert) return;
        if (shouldAnimateOut || (animation && animation.out)) return;

        splitRefChild.current?.revert();
        if (type === "lines") splitRefParent.current?.revert();
      },
    });
  }

  function animateOut(params = {}) {
    visible.current = false;
    gsap.to(splitRefChild.current?.[type], {
      //opacity: 1,
      ...(animation?.out
        ? animation.out(params)
        : animations[animationType].out(params)),
      ...params,
      onComplete: () => {
        params?.onComplete?.();
        setAnimateIn();
      },
    });
  }

  useScrollRigScrollTrigger({
    el: animateOnScroll ? localRef : null,
    handleIn: () => {
      if (!animateOnScroll) return;
      animateIn(onScrollParams);
    },
    handleOut: () => {
      // if (!animateOnScroll) return;
      // animateOut();
    },
  });

  function revert() {
    splitRefChild.current?.revert();
    if (type === "lines") splitRefParent.current?.revert();

    splitRefChild.current = null;
    splitRefParent.current = null;
  }

  function setup() {
    splitRefChild.current = new SplitText(localRef.current, {
      type,
      linesClass: "splittext-line-child",
    });

    if (type === "lines")
      splitRefParent.current = new SplitText(localRef.current, {
        type,
        linesClass: "splittext-line-parent",
      });

    setAnimateIn();
  }

  useIsomorphicLayoutEffect(() => {
    setup();

    return () => {
      revert();
    };
  }, [tag, type]);

  useImperativeHandle(ref, () => ({
    animateIn,
    animateOut,
    setAnimateIn,
    element: localRef.current,
    setText: (text) => {
      localRef.current.innerText = text;
    },
    revert,
    setup,
  }));

  useResize(
    () => {
      revert();
      setup();

      //localRef.current.style.minHeight = "none";

      if (visible.current) {
        animateIn({ duration: 0 });
      }
    },
    {
      debounce: 50,
    },
    [localRef]
  );

  return createElement(
    tag,
    {
      ref: localRef,
      className,
      ...props,
    },
    children
  );
}

export default forwardRef(CustomSplitText);
